const PDP_MOBILE_MQ = window.matchMedia("(max-width: 989px)");

class PdpMobileStickyBar extends HTMLElement {
  constructor() {
    super();
    this.visibleClass = "pdp-mobile-sticky--visible";
    this.bodyActiveClass = "pdp-mobile-sticky-active";
    this.isSyncingQty = false;
    this.observer = null;
    this.footerObserver = null;
    this.mobileEnabled = false;
    this.buyButtonsInView = true;
    this.footerInView = false;
    this.onResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.formId;
    this.mainForm = document.getElementById(this.formId);
    this.buyButtons = document.querySelector(
      `#shopify-section-${this.sectionId} .main-product__buy-buttons--container`,
    );
    this.mainSubmit = this.mainForm?.querySelector('[name="add"]');
    this.mainSubmitText = this.mainSubmit?.querySelector("span");
    this.stickyAdd = this.querySelector(".js-pdp-sticky-add");
    this.stickyAddText = this.querySelector(".pdp-mobile-sticky__add-text");
    this.stickyBuyNow = this.querySelector(".js-pdp-sticky-buy-now");
    this.mainQty = this.mainForm?.querySelector(".js-counter-quantity");
    this.stickyQty = this.querySelector(".js-counter-quantity");

    if (!this.mainForm || !this.stickyAdd) return;

    this.bindActions();
    this.bindQuantitySync();
    this.bindSubmitObserver();
    this.bindLoadingSync();
    this.bindVariantSync();
    window.addEventListener("resize", this.onResize);
    this.handleResize();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    this.observer?.disconnect();
    this.footerObserver?.disconnect();
    this.submitObserver?.disconnect();
    this.loadingObserver?.disconnect();
    this.variantUnsubscribe?.();
    document.body.classList.remove(this.bodyActiveClass);
  }

  handleResize() {
    if (PDP_MOBILE_MQ.matches) {
      this.enableMobile();
    } else {
      this.disableMobile();
    }
  }

  enableMobile() {
    if (this.mobileEnabled || !this.buyButtons) return;

    this.mobileEnabled = true;
    this.buyButtonsInView = true;
    this.footerInView = false;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.buyButtonsInView = entry.isIntersecting;
        this.updateBarVisibility();
      },
      { threshold: 0, rootMargin: "0px 0px 8px 0px" },
    );

    this.observer.observe(this.buyButtons);

    const footer = document.querySelector(
      "#shopify-section-group-footer-group, footer[class*='ai-footer-']",
    );

    if (footer) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.footerInView = entry.isIntersecting;
          this.updateBarVisibility();
        },
        { threshold: 0, rootMargin: "0px 0px 0px 0px" },
      );

      this.footerObserver.observe(footer);
    }

    this.updateBarVisibility();
  }

  updateBarVisibility() {
    const show = !this.buyButtonsInView && !this.footerInView;
    const wasVisible = this.classList.contains(this.visibleClass);

    this.classList.toggle(this.visibleClass, show);
    this.setAttribute("aria-hidden", show ? "false" : "true");
    document.body.classList.toggle(this.bodyActiveClass, show);

    if (show && !wasVisible) {
      this.playEnterAnimation();
    }
  }

  disableMobile() {
    this.mobileEnabled = false;
    this.observer?.disconnect();
    this.observer = null;
    this.footerObserver?.disconnect();
    this.footerObserver = null;
    this.classList.remove(this.visibleClass);
    this.setAttribute("aria-hidden", "true");
    document.body.classList.remove(this.bodyActiveClass);
  }

  playEnterAnimation() {
    this.querySelectorAll(".pdp-mobile-sticky__item").forEach((el) => {
      el.style.animation = "none";
      void el.offsetHeight;
      el.style.animation = "";
    });
  }

  bumpQuantity() {
    if (!this.stickyQty) return;
    this.stickyQty.classList.remove("pdp-qty-bump");
    void this.stickyQty.offsetWidth;
    this.stickyQty.classList.add("pdp-qty-bump");
    this.stickyQty.addEventListener(
      "animationend",
      () => this.stickyQty.classList.remove("pdp-qty-bump"),
      { once: true },
    );
  }

  bindActions() {
    this.stickyAdd.addEventListener("click", () => {
      if (this.stickyAdd.disabled) return;
      this.syncQuantityToMain();
      this.mainSubmit?.click();
    });

    this.stickyBuyNow?.addEventListener("click", () => {
      this.handleBuyNow();
    });
  }

  getCheckoutUrl() {
    if (window.Shopify?.routes?.checkout) {
      return window.Shopify.routes.checkout;
    }

    if (window.routes?.checkout_url) {
      return window.PreviewCheckout?.isThemePreview?.()
        ? window.PreviewCheckout.liveCheckoutUrl(window.routes.checkout_url)
        : window.routes.checkout_url;
    }

    const root = window.Shopify?.routes?.root || "/";
    const normalized = root.endsWith("/") ? root : `${root}/`;
    const checkoutPath = `${normalized}checkout`;

    return window.PreviewCheckout?.isThemePreview?.()
      ? window.PreviewCheckout.liveCheckoutUrl(checkoutPath)
      : checkoutPath;
  }

  async handleBuyNow() {
    if (!this.stickyBuyNow || this.stickyBuyNow.disabled) return;

    this.syncQuantityToMain();

    const paymentButton = this.mainForm.querySelector(
      ".shopify-payment-button__button:not([disabled])",
    );

    if (paymentButton) {
      paymentButton.click();
      return;
    }

    const variantId = this.mainForm.querySelector('[name="id"]')?.value;
    const quantity = parseInt(
      this.mainForm.querySelector('[name="quantity"]')?.value ||
        this.stickyQty?.value ||
        "1",
      10,
    );

    if (!variantId) return;

    this.stickyBuyNow.disabled = true;
    this.stickyBuyNow.classList.add("is-loading");

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: quantity || 1 }],
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.description || data?.message || window.cartStrings?.error || "Cart add failed");
      }

      window.location.href = this.getCheckoutUrl();
    } catch (error) {
      console.error("Buy now failed:", error);
      this.mainSubmit?.click();
      this.stickyBuyNow.disabled = false;
      this.stickyBuyNow.classList.remove("is-loading");
    }
  }

  syncQuantityToMain() {
    if (!this.stickyQty) return;

    if (this.mainQty) {
      if (this.mainQty.value !== this.stickyQty.value) {
        this.mainQty.value = this.stickyQty.value;
        this.mainQty.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    let hiddenQty = this.mainForm.querySelector('input[name="quantity"]');
    if (!hiddenQty) {
      hiddenQty = document.createElement("input");
      hiddenQty.type = "hidden";
      hiddenQty.name = "quantity";
      this.mainForm.appendChild(hiddenQty);
    }
    hiddenQty.value = this.stickyQty.value;
  }

  syncQuantityFromMain() {
    if (!this.mainQty || !this.stickyQty || this.isSyncingQty) return;
    if (this.stickyQty.value !== this.mainQty.value) {
      this.isSyncingQty = true;
      this.stickyQty.value = this.mainQty.value;
      this.isSyncingQty = false;
      this.bumpQuantity();
    }
  }

  syncQuantityToSticky() {
    if (!this.mainQty || !this.stickyQty || this.isSyncingQty) return;
    if (this.mainQty.value !== this.stickyQty.value) {
      this.isSyncingQty = true;
      this.mainQty.value = this.stickyQty.value;
      this.mainQty.dispatchEvent(new Event("change", { bubbles: true }));
      this.isSyncingQty = false;
      this.bumpQuantity();
    }
  }

  bindQuantitySync() {
    if (!this.stickyQty) return;

    if (this.mainQty) {
      this.mainQty.addEventListener("change", () => this.syncQuantityFromMain());
    }

    this.stickyQty.addEventListener("change", () => {
      if (this.mainQty) {
        this.syncQuantityToSticky();
      } else {
        this.bumpQuantity();
      }
    });

    this.stickyQty
      .closest("quantity-counter")
      ?.querySelector(".js-counter-increase")
      ?.addEventListener("click", () => requestAnimationFrame(() => this.bumpQuantity()));

    this.stickyQty
      .closest("quantity-counter")
      ?.querySelector(".js-counter-decrease")
      ?.addEventListener("click", () => requestAnimationFrame(() => this.bumpQuantity()));

    this.syncQuantityFromMain();
  }

  syncButtonState() {
    if (!this.mainSubmit || !this.stickyAdd) return;

    const disabled = this.mainSubmit.hasAttribute("disabled");
    this.stickyAdd.toggleAttribute("disabled", disabled);
    this.stickyBuyNow?.toggleAttribute("disabled", disabled);

    if (this.mainSubmitText && this.stickyAddText) {
      this.stickyAddText.textContent = this.mainSubmitText.textContent;
    }
  }

  bindSubmitObserver() {
    if (!this.mainSubmit) return;

    this.syncButtonState();
    this.submitObserver = new MutationObserver(() => this.syncButtonState());
    this.submitObserver.observe(this.mainSubmit, {
      attributes: true,
      attributeFilter: ["disabled"],
      subtree: true,
      characterData: true,
      childList: true,
    });

    if (this.mainSubmitText) {
      this.submitObserver.observe(this.mainSubmitText, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
  }

  bindLoadingSync() {
    if (!this.mainSubmit || !this.stickyAdd) return;

    const syncLoading = () => {
      this.stickyAdd.classList.toggle(
        "is-loading",
        this.mainSubmit.classList.contains("loading"),
      );
    };

    syncLoading();
    this.loadingObserver = new MutationObserver(syncLoading);
    this.loadingObserver.observe(this.mainSubmit, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  bindVariantSync() {
    if (typeof subscribe !== "function") return;

    this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(() => this.syncButtonState());
    });
  }
}

customElements.define("pdp-mobile-sticky-bar", PdpMobileStickyBar);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.className === "wt-product__img") {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll("img"),
            );
            const index = thumbnails.indexOf(event.target);

            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
